import * as React from 'react';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { InsuranceType } from '@websky/graphql';
import { canDownloadInsurancePolicy } from '@shared/lib/insurance';
import { Banner } from '../Banner/Banner';
import { Card } from '../Card/Card';
import { EngineTheme, useMergeClasses, useTheme } from '../../../theme';
import IconButton from '../../../BaseComponents/Button/components/IconButton/IconButton';
import { InsurancePrice } from '../InsurancePrice/InsurancePrice';
import { SelectForm } from '../SelectForm/SelectForm';
import Link from '../../../Link';
import { OptionsList } from '../OptionsList/OptionsList';
import { isExistI18nTranslate } from '../../../utils';
import { InsuranceProductViewBaseProps } from './types';
import { CdnIcon } from '@shared/ui';

interface Props extends InsuranceProductViewBaseProps {
	classes?: EngineTheme['InsurancePage']['InsuranceProduct'];
}

export const InsuranceProductView: FC<Props> = ({
	insuranceProgram,
	className,
	classes,
	popular,
	policyUrl,
	onSelect,
	onRemove,
	onDownloadVskInsurance,
	hideForAllPassengersTextBecauseSinglePassenger,
	isShowImportantInsurancesError,
	bannerVariant = 'icon',
	popularPricePosition = 'withinRadio',
	showIconButton,
	isSelected,
	isConfirmed,
	isTouched,
	isError,
	handleToggle
}) => {
	const { t } = useTranslation('InsurancePage');
	const { InsuranceProduct: defaultTheme } = useTheme('InsurancePage');
	const theme = useMergeClasses(defaultTheme, classes);

	const [isLoading, setIsLoading] = useState(false);

	const onDownloadVskInsuranceHandler = useCallback(async () => {
		setIsLoading(true);
		await onDownloadVskInsurance?.();
		setIsLoading(false);
	}, [onDownloadVskInsurance]);

	const infoLink = useMemo(() => {
		const isVskInsurance = insuranceProgram.type === InsuranceType.Vsk;
		const isConfirmedAndCanDownload = canDownloadInsurancePolicy({
			isConfirmed,
			policyUrl,
			insuranceType: insuranceProgram.type
		});

		if (!insuranceProgram.urlToOffer && !isConfirmedAndCanDownload) {
			return null;
		}

		return (
			<div className={theme.linksWrapper}>
				{isExistI18nTranslate('Offer', { ns: 'InsurancePage' }) ? (
					<div className={theme.moreInfo}>
						<CdnIcon icon="Info/Details" className={theme.infoIcon} />

						<span>{t('More details in')}&nbsp;</span>
						<Link className={theme.moreInfo} action={insuranceProgram.urlToOffer} target="_blank">
							{t('Offer')}
						</Link>
					</div>
				) : (
					<Link className={theme.moreInfo} action={insuranceProgram.urlToOffer} target={'_blank'}>
						<CdnIcon icon="Info/Details" className={theme.infoIcon} />

						{t('More details')}
					</Link>
				)}

				{isConfirmedAndCanDownload && (
					<Link
						className={cn(theme.moreInfo, { [theme.moreInfo_loading]: isLoading })}
						action={isVskInsurance ? onDownloadVskInsuranceHandler : policyUrl}
						target={!isVskInsurance ? '_blank' : undefined}
					>
						<CdnIcon icon="Action/Download" className={theme.infoIcon} />

						{t('Download police')}
					</Link>
				)}
			</div>
		);
	}, [
		insuranceProgram.urlToOffer,
		insuranceProgram.type,
		policyUrl,
		isConfirmed,
		onDownloadVskInsuranceHandler,
		isLoading
	]);

	return (
		<Card
			className={cn(
				theme.card,
				theme.border,
				theme.shadow,
				{
					[theme.popular]: popular,
					[theme.confirmed]: isConfirmed
				},
				className
			)}
		>
			<Banner
				className={theme.banner}
				popular={popular}
				title={insuranceProgram.name}
				code={insuranceProgram.code}
				type={insuranceProgram.type}
				variant={bannerVariant}
			/>

			<div className={theme.content}>
				{showIconButton && (
					<IconButton
						onClick={handleToggle}
						isSuccess={isSelected || isConfirmed}
						className={cn(theme.selectButton, {
							[theme.confirmed]: isConfirmed
						})}
					>
						<CdnIcon icon="Action/Add" />
					</IconButton>
				)}

				<div className={theme.contentWrapper}>
					{bannerVariant === 'icon' && <div className={theme.title}>{insuranceProgram.name}</div>}
					{insuranceProgram.description && (
						<div
							className={theme.description}
							dangerouslySetInnerHTML={{ __html: insuranceProgram.description }}
						/>
					)}
					{!!insuranceProgram.options?.length && (
						<OptionsList
							className={theme.options}
							options={insuranceProgram.options.map(({ name }) => name)}
						/>
					)}
				</div>
			</div>

			<div className={theme.footer}>
				{infoLink}
				{popular && !isConfirmed && (
					<SelectForm
						className={theme.formControl}
						onSelect={() => onSelect(insuranceProgram.code)}
						onRemove={() => onRemove(insuranceProgram.code)}
						selected={isSelected}
						hideForAllPassengersTextBecauseSinglePassenger={hideForAllPassengersTextBecauseSinglePassenger}
						isTouched={isTouched}
						isError={isError && isShowImportantInsurancesError}
						price={popularPricePosition === 'withinRadio' ? insuranceProgram.totalPrice : undefined}
					/>
				)}

				{popular && popularPricePosition === 'bottom' && (
					<InsurancePrice
						selected={isSelected}
						price={insuranceProgram.totalPrice}
						variant="bottomPrice"
						hideForAllPassengersTextBecauseSinglePassenger={false}
					/>
				)}
				{!popular && (
					<InsurancePrice
						className={theme.footerPrice}
						selected={isSelected}
						price={insuranceProgram.totalPrice}
						variant="regular"
						onClick={!isConfirmed ? handleToggle : undefined}
						onRemove={handleToggle}
						hideForAllPassengersTextBecauseSinglePassenger={hideForAllPassengersTextBecauseSinglePassenger}
					/>
				)}
			</div>
		</Card>
	);
};
